import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, ShieldCheck } from 'lucide-react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const { login, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Honour `?next=…` (set by deep-links from emails) or location.state.from
  // (set by ProtectedRoute) so users who deep-link to an asset land back on
  // that asset after auth.
  const nextParam = new URLSearchParams(location.search).get('next');
  const fromState = location.state?.from?.pathname
    ? `${location.state.from.pathname}${location.state.from.search || ''}`
    : null;
  const intendedNext = nextParam || fromState;

  // Redirect when authenticated (handles both initial load and post-login)
  useEffect(() => {
    if (isAuthenticated && user) {
      // Only honour `next` for regular elite users - admins/partners always
      // go to their own dashboards to avoid permission landing-page weirdness.
      const isPrivilegedRole = user.role === 'admin' || user.role === 'partner';
      let destination;
      if (intendedNext && !isPrivilegedRole) {
        destination = intendedNext;
      } else {
        destination =
          user.role === 'admin' ? '/admin' :
          user.role === 'partner' ? '/partner' :
          '/dashboard';
      }
      console.log(`Login: redirecting to ${destination}`);
      navigate(destination, { replace: true });
    }
  }, [isAuthenticated, user, navigate, intendedNext]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      console.log('Attempting login...');
      const result = await login(email, password, requires2FA ? twoFactorCode : null);
      
      // Check if 2FA is required
      if (result?.requires_2fa) {
        setRequires2FA(true);
        setLoading(false);
        toast.info('Please enter your two-factor authentication code');
        return;
      }
      
      console.log('Login successful, user:', result);
      toast.success('Welcome back to Assagart');
      // Navigation is handled by the useEffect when isAuthenticated becomes true
      // This ensures state is fully propagated before redirect
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Invalid credentials');
      setLoading(false);
    }
  };

  const handleBack = () => {
    setRequires2FA(false);
    setTwoFactorCode('');
    setPassword('');
  };

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Left side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img 
          src="https://customer-assets.emergentagent.com/job_61ee5a3a-0e3f-4193-8c04-5f0d789b4adf/artifacts/u57f93li_ChatGPT%20Image%20May%2011%2C%202026%2C%2010_43_04%20AM.png"
          alt="Cartier and Van Cleef jewellery on silk"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/50" />
        <div className="absolute bottom-12 left-12 right-12">
          <p className="text-gold uppercase tracking-[0.3em] text-xs mb-4">Welcome Back</p>
          <h2 className="font-serif text-4xl text-white leading-tight">
            Your assets await.
          </h2>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <Link to="/" className="flex items-center mb-12">
            <img 
              src="/assagart-logo.png?v=2" 
              alt="Assagart" 
              className="h-16 w-auto"
            />
          </Link>

          <h1 className="font-serif text-3xl text-white mb-2">
            {requires2FA ? 'Two-Factor Authentication' : 'Sign In'}
          </h1>
          <p className="text-muted-foreground mb-8">
            {requires2FA ? 'Enter the code from your authenticator app' : 'Access your private vault'}
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            {!requires2FA ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm uppercase tracking-wider text-muted-foreground">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    data-testid="login-email-input"
                    className="luxury-input w-full"
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-sm uppercase tracking-wider text-muted-foreground">
                      Password
                    </Label>
                    <Link to="/forgot-password" className="text-xs text-gold hover:underline" data-testid="forgot-password-link">
                      Forgot password?
                    </Link>
                  </div>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      data-testid="login-password-input"
                      className="luxury-input w-full pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="space-y-4">
                <div className="flex justify-center mb-4">
                  <div className="p-4 bg-gold/10 rounded-full">
                    <ShieldCheck size={32} className="text-gold" />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="2fa-code" className="text-sm uppercase tracking-wider text-muted-foreground">
                    Authentication Code
                  </Label>
                  <Input
                    id="2fa-code"
                    type="text"
                    value={twoFactorCode}
                    onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    required
                    autoFocus
                    data-testid="login-2fa-input"
                    className="luxury-input w-full text-center text-2xl tracking-[0.5em] font-mono"
                    maxLength={6}
                  />
                </div>
                
                <p className="text-xs text-muted-foreground text-center">
                  Open your authenticator app and enter the 6-digit code
                </p>
              </div>
            )}

            <Button 
              type="submit" 
              className="btn-primary w-full"
              disabled={loading || (requires2FA && twoFactorCode.length !== 6)}
              data-testid="login-submit-btn"
            >
              {loading ? 'Verifying...' : requires2FA ? 'Verify Code' : 'Sign In'}
            </Button>
            
            {requires2FA && (
              <Button 
                type="button"
                variant="ghost"
                className="w-full text-muted-foreground hover:text-white"
                onClick={handleBack}
              >
                Back to Sign In
              </Button>
            )}
          </form>

          <p className="mt-8 text-center text-sm text-muted-foreground">
            Don't have an account?{' '}
            <Link to="/register" className="text-gold hover:underline" data-testid="login-register-link">
              Create one
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
